import classnames from 'classnames'
import Badge from 'components/Project/Project/Step/Badge/Badge'
import NavigationContainer from 'components/Project/Project/Step/Navigation/NavigationContainer'
import { pathwayPath } from 'helpers/Pathway'
import { isChallenge, isReflection } from 'helpers/Step'
import { disableIframeContent } from 'helpers/cookieBot'
import localForage from 'localforage'
import PropTypes from 'prop-types'
import { Wysiwyg } from 'raspberry-pi-bits'
import React, { Component } from 'react'
import { withNamespaces } from 'react-i18next'
import 'swiper/dist/css/swiper.min.css'

import './StepContent.scss'

export class StepContent extends Component {
  async componentDidMount() {
    await this.updateTasksCheckboxes()
  }

  checkboxAriaLabel = (checkbox, index) => {
    let status = this.props.t('project.steps.taskNotCompleted')
    if (checkbox.checked) {
      status = this.props.t('project.steps.taskCompleted')
    }

    checkbox.ariaLabel = this.props.t('project.steps.taskCheckboxAriaLabel', {
      title: this.props.step.title,
      number: index + 1,
      status,
    })
  }

  addCheckboxEventListener = (checkbox) => {
    checkbox.addEventListener('change', (event) => {
      const checked = event.target.checked
      const taskIndex = parseInt(checkbox.dataset.taskIndex, 10)
      this.checkboxAriaLabel(checkbox, taskIndex)

      this.updateTasksProgress(taskIndex, checked)
    })

    checkbox.dataset.onChangeEventListenerAdded = true
  }

  async loadTasksProgress() {
    if (this.tasksCount() === 0) {
      return null
    }
    let progress = await localForage.getItem(this.tasksProgressKey())

    if (progress) {
      return JSON.parse(progress)
    }

    return await this.initTasksProgress()
  }

  tasksCount = () => {
    let content = this.props.step.content
    return (content.match(/"(c-project-task)+[ "]/g) || []).length
  }

  async initTasksProgress() {
    let progress = new Array(this.tasksCount())
    progress.fill(false)

    await this.saveTasksProgress(progress)

    return progress
  }

  tasksProgressKey = () => {
    const { slug, step } = this.props
    return `tasks-progress-${slug}-step-${step.position}`
  }

  async saveTasksProgress(progress) {
    await localForage.setItem(this.tasksProgressKey(), JSON.stringify(progress))
  }

  async updateTasksProgress(index, value) {
    let progress = await this.loadTasksProgress()
    progress[index] = value
    await this.saveTasksProgress(progress)
  }

  async updateTasksCheckboxes() {
    if (this.tasksCount() === 0) {
      return
    }
    const progress = await this.loadTasksProgress()

    let checkboxes = document.querySelectorAll('.c-project-task__checkbox')
    let checkbox

    for (let index = 0; index < checkboxes.length; index += 1) {
      checkbox = checkboxes[index]
      checkbox.dataset.taskIndex = index

      if (progress[index]) {
        checkbox.checked = true
      }

      this.checkboxAriaLabel(checkbox, index)

      this.addCheckboxEventListener(checkbox)
    }
  }

  getIntroduction = ({ step, pathway, pathwayStep, t, locale }) => {
    if (step.position === 0 && pathway && pathwayStep) {
      const { category } = pathwayStep
      if (category && pathway.isBeta) {
        const { title, slug } = pathway
        const url = pathwayPath(locale, slug)
        const { position, total } = pathwayStep

        return (
          <div className="c-wysiwyg">
            <h2>{t('project.steps.introduction.heading')}</h2>
            <div
              dangerouslySetInnerHTML={{
                __html: t(`project.steps.introduction.${category}Text`, {
                  title,
                  position,
                  total,
                  url,
                }),
              }}
            />
          </div>
        )
      }
    }
    return null
  }

  renderContent = ({ step, archived, progressCallback, hasBadge }) => {
    const {
      props: { t, pathway, pathwayStep, locale },
    } = this

    const stepIsChallenge = isChallenge(step)
    const classNames = classnames('c-project-steps__wrapper', {
      'c-project-steps__wrapper--challenge': stepIsChallenge,
    })
    const challengeLabel = stepIsChallenge ? (
      <span className="c-project-steps__challenge-label">
        {t('project.steps.challenge')}
      </span>
    ) : null

    const replaceHttp = (content) => {
      if (!content) return
      return content.replace(/(http:)/gm, 'https:')
    }

    return (
      <div>
        {archived && (
          <>
            {this.getIntroduction({ step, pathway, pathwayStep, t, locale })}
            <div
              className="c-project-steps__archived"
              dangerouslySetInnerHTML={{
                __html: t('project.steps.archived'),
              }}
            />
          </>
        )}
        <div className={classNames} id="skiptocontent">
          {challengeLabel}
          {this.getIntroduction({ step, pathway, pathwayStep, t, locale })}
          <Wysiwyg>
            <div
              className="c-project-steps__content"
              dangerouslySetInnerHTML={{
                __html: disableIframeContent(
                  replaceHttp(step.content),
                  this.props.t
                ),
              }}
            />
          </Wysiwyg>
          {isReflection(step) && hasBadge && (
            <Badge
              progressCallback={progressCallback}
              badge={this.props.badge}
              projectTitle={this.props.projectTitle}
              slug={this.props.slug}
            />
          )}
          <NavigationContainer />
        </div>
      </div>
    )
  }

  render() {
    return <React.Fragment>{this.renderContent(this.props)}</React.Fragment>
  }
}

StepContent.propTypes = {
  badge: PropTypes.object,
  locale: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  step: PropTypes.object.isRequired,
  projectTitle: PropTypes.string,
  t: PropTypes.func.isRequired,
  archived: PropTypes.bool.isRequired,
  progressCallback: PropTypes.func.isRequired,
  hasBadge: PropTypes.bool.isRequired,
  pathway: PropTypes.object,
  pathwayStep: PropTypes.object,
}

export default withNamespaces('translations')(StepContent)
